/**
 * Subscription plan feature access from login payment_setting.plan_features.
 */

export const PLAN_FEATURE_REASONS = {
  UPGRADE_REQUIRED: "upgrade_required",
  NOT_CONFIGURED: "not_configured",
  GRANDFATHERED_READONLY: "grandfathered_readonly",
  AT_CAP: "at_cap",
  OVER_CAP_GRANDFATHERED: "over_cap_grandfathered",
};

/** Default role cap when plan tier is unknown (trial / stale session). */
export const DEFAULT_TOTAL_SUITE_ROLE_LIMIT = 5;

/** Elevated / Platinum only — default deny when plan snapshot is missing. */
const TIER_GATED_FEATURES = new Set([
  "family_login",
  "participant_login",
  "in_app_chat",
]);

function isTotalSuitePackageLabel(label) {
  const normalized = String(label || "").trim().toLowerCase();
  return (
    normalized.includes("total") && normalized.includes("suite")
  );
}

/** Enterprise wildcard / comp billing — full plan feature access. */
function isWildcardPaymentSetting(paymentSetting) {
  const flag = paymentSetting?.wildcard;
  return flag === true || flag === 1 || flag === "1" || flag === "true";
}

/** Unwrap Laravel JsonResource `data` wrapper if present on stored payment_setting. */
export function normalizePaymentSetting(paymentSetting) {
  if (!paymentSetting || typeof paymentSetting !== "object") {
    return null;
  }
  if (paymentSetting.plan_features) {
    return paymentSetting;
  }
  if (paymentSetting.data && typeof paymentSetting.data === "object") {
    return {
      ...paymentSetting,
      ...paymentSetting.data,
      plan_features:
        paymentSetting.data.plan_features ?? paymentSetting.plan_features,
    };
  }
  return paymentSetting;
}

export function mergePaymentSetting(existing, incoming) {
  const normalizedIncoming = normalizePaymentSetting(incoming);
  if (!normalizedIncoming) {
    return existing ?? null;
  }
  const normalizedExisting = normalizePaymentSetting(existing);
  const planFeatures =
    normalizedIncoming.plan_features ??
    normalizedExisting?.plan_features ??
    null;

  return {
    ...(normalizedExisting || {}),
    ...normalizedIncoming,
    plan_features: planFeatures,
  };
}

export function getPlanFeatures(user) {
  const ps = normalizePaymentSetting(user?.payment_setting);
  return ps?.plan_features ?? null;
}

function tierBlocksRegionsFeature(planFeatures, paymentSetting) {
  if (isWildcardPaymentSetting(paymentSetting)) {
    return false;
  }
  if (planFeatures?.pre_elevated_tier_account === true) {
    return false;
  }
  const subscribedTier =
    planFeatures?.subscription_tier ?? planFeatures?.tier ?? null;
  if (subscribedTier === "elevated" || subscribedTier === "platinum") {
    return false;
  }
  if (subscribedTier === "total_suite") {
    return true;
  }
  const packageLabel =
    paymentSetting?.internal_package_name ??
    planFeatures?.subscription_tier_label ??
    planFeatures?.tier_label ??
    "";
  if (isTotalSuitePackageLabel(packageLabel)) {
    return true;
  }
  return subscribedTier == null;
}

function tierBlocksPremiumFeature(planFeatures, paymentSetting, featureKey) {
  if (isWildcardPaymentSetting(paymentSetting)) {
    return false;
  }
  if (!TIER_GATED_FEATURES.has(featureKey)) {
    return false;
  }
  const subscribedTier =
    planFeatures?.subscription_tier ?? planFeatures?.tier ?? null;
  if (
    subscribedTier === "total_suite" ||
    planFeatures?.tier === "total_suite"
  ) {
    return true;
  }
  const packageLabel =
    paymentSetting?.internal_package_name ??
    planFeatures?.subscription_tier_label ??
    planFeatures?.tier_label ??
    "";
  return isTotalSuitePackageLabel(packageLabel);
}

export function getPlanFeature(user, featureKey) {
  const paymentSetting = normalizePaymentSetting(user?.payment_setting);
  const planFeatures = paymentSetting?.plan_features ?? null;

  if (tierBlocksPremiumFeature(planFeatures, paymentSetting, featureKey)) {
    return {
      allowed: false,
      can_create: false,
      reason: PLAN_FEATURE_REASONS.UPGRADE_REQUIRED,
    };
  }

  if (
    featureKey === "regions" &&
    tierBlocksRegionsFeature(planFeatures, paymentSetting)
  ) {
    return {
      allowed: false,
      can_create: false,
      reason: planFeatures
        ? PLAN_FEATURE_REASONS.UPGRADE_REQUIRED
        : PLAN_FEATURE_REASONS.NOT_CONFIGURED,
    };
  }

  if (!planFeatures) {
    if (TIER_GATED_FEATURES.has(featureKey)) {
      return {
        allowed: false,
        can_create: false,
        reason: PLAN_FEATURE_REASONS.NOT_CONFIGURED,
      };
    }
    return { allowed: true, can_create: true };
  }

  const feature = planFeatures[featureKey];
  if (!feature) {
    if (TIER_GATED_FEATURES.has(featureKey)) {
      return {
        allowed: false,
        can_create: false,
        reason: PLAN_FEATURE_REASONS.UPGRADE_REQUIRED,
      };
    }
    return { allowed: true, can_create: true };
  }

  return feature;
}

export function isPlanFeatureAllowed(user, featureKey) {
  const feature = getPlanFeature(user, featureKey);
  return feature?.allowed !== false;
}

function isPremiumLoginFeatureAllowed(user, featureKey) {
  const paymentSetting = normalizePaymentSetting(user?.payment_setting);
  if (!paymentSetting) {
    return false;
  }

  if (isWildcardPaymentSetting(paymentSetting)) {
    return true;
  }

  const planFeatures = paymentSetting.plan_features;
  if (planFeatures?.[featureKey]?.allowed === false) {
    return false;
  }

  if (tierBlocksPremiumFeature(planFeatures, paymentSetting, featureKey)) {
    return false;
  }

  if (planFeatures?.[featureKey]?.allowed === true) {
    return true;
  }

  const subscribedTier =
    planFeatures?.subscription_tier ?? planFeatures?.tier ?? null;
  if (subscribedTier === "elevated" || subscribedTier === "platinum") {
    return planFeatures?.[featureKey]?.allowed !== false;
  }

  return false;
}

/** Strict gate for Family App (Elevated / Platinum only). */
export function isFamilyLoginAllowed(user) {
  return isPremiumLoginFeatureAllowed(user, "family_login");
}

/** Strict gate for Participant App (Elevated / Platinum only). */
export function isParticipantLoginAllowed(user) {
  return isPremiumLoginFeatureAllowed(user, "participant_login");
}

/** Participant + family mobile login (same tier rules). */
export function isFamilyAppLoginAllowed(user) {
  return (
    isParticipantLoginAllowed(user) && isFamilyLoginAllowed(user)
  );
}

export function getFamilyAppLoginUpgradeMessage() {
  return "Participant and family app login are available on Elevated and Platinum plans.";
}

export function canCreatePlanFeature(user, featureKey) {
  const feature = getPlanFeature(user, featureKey);
  if (feature?.allowed === false) {
    return false;
  }
  return feature?.can_create !== false;
}

export function getRolePlanFeature(user, portalType) {
  const planFeatures = getPlanFeatures(user);
  const paymentSetting = normalizePaymentSetting(user?.payment_setting);
  if (
    planFeatures?.pre_elevated_tier_account === true ||
    isWildcardPaymentSetting(paymentSetting)
  ) {
    return planFeatures?.roles?.[portalType] ?? { can_create: true, limit: null };
  }
  const roleDto = planFeatures?.roles?.[portalType];
  if (roleDto) {
    return roleDto;
  }
  if (!planFeatures) {
    return {
      limit: DEFAULT_TOTAL_SUITE_ROLE_LIMIT,
      current: 0,
      can_create: true,
      reason: null,
    };
  }
  if (planFeatures.tier == null && planFeatures.subscription_tier == null) {
    return {
      limit: DEFAULT_TOTAL_SUITE_ROLE_LIMIT,
      current: 0,
      can_create: true,
      reason: null,
    };
  }
  return { can_create: true };
}

export function countRolesForPortal(rolesList, portalType) {
  if (!Array.isArray(rolesList) || !portalType) {
    return null;
  }
  return rolesList.filter((role) => role?.type === portalType).length;
}

/**
 * Merge login plan_features with live role list counts (roles page).
 */
export function resolveRolePlanFeature(user, portalType, rolesList) {
  const base = getRolePlanFeature(user, portalType);
  const planFeatures = getPlanFeatures(user);
  const liveCount = countRolesForPortal(rolesList, portalType);

  const paymentSetting = normalizePaymentSetting(user?.payment_setting);
  if (
    planFeatures?.pre_elevated_tier_account === true ||
    isWildcardPaymentSetting(paymentSetting)
  ) {
    return {
      ...base,
      limit: null,
      current: liveCount ?? base?.current ?? 0,
      can_create: true,
      reason: null,
    };
  }

  if (liveCount == null) {
    return base;
  }

  if (base?.limit == null) {
    return { ...base, current: liveCount, can_create: base?.can_create !== false };
  }

  const atCap = liveCount >= base.limit;
  const overCap = liveCount > base.limit;
  const preElevated = planFeatures?.pre_elevated_tier_account === true;
  const canCreate = liveCount < base.limit;

  let reason = null;
  if (!canCreate) {
    reason =
      overCap && preElevated
        ? PLAN_FEATURE_REASONS.OVER_CAP_GRANDFATHERED
        : PLAN_FEATURE_REASONS.AT_CAP;
  }

  return {
    ...base,
    current: liveCount,
    can_create: canCreate,
    reason,
  };
}

export function canCreateRoleForPortal(user, portalType, rolesList = null) {
  return resolveRolePlanFeature(user, portalType, rolesList)?.can_create !== false;
}

/** Update one portal's role cap snapshot on the auth user. */
export function patchSinglePortalRolePlanFeature(user, portalType, rolesList) {
  if (
    !portalType ||
    !user?.payment_setting?.plan_features?.roles?.[portalType] ||
    !Array.isArray(rolesList)
  ) {
    return user;
  }

  const resolved = resolveRolePlanFeature(user, portalType, rolesList);
  const updated = JSON.parse(JSON.stringify(user));
  updated.payment_setting.plan_features.roles[portalType] = {
    ...updated.payment_setting.plan_features.roles[portalType],
    current: resolved.current,
    can_create: resolved.can_create,
    reason: resolved.reason,
  };

  return updated;
}

/** Keep login payment_setting.plan_features in sync after role create/list changes. */
export function patchPlanFeaturesRoleCounts(user, rolesList, portalType = null) {
  if (!Array.isArray(rolesList)) {
    return user;
  }

  if (portalType) {
    return patchSinglePortalRolePlanFeature(user, portalType, rolesList);
  }

  return ["blue_portal", "black_portal"].reduce(
    (acc, pt) => patchSinglePortalRolePlanFeature(acc, pt, rolesList),
    user
  );
}

export function getPlanFeatureUpgradeMessage(featureKey, featureDto) {
  const reason = featureDto?.reason;

  if (featureKey === "regions") {
    if (reason === PLAN_FEATURE_REASONS.GRANDFATHERED_READONLY) {
      return "Your existing regions remain active. Upgrade to Elevated or Platinum to add new regions.";
    }
    return "Regions are available on Elevated and Platinum plans. Upgrade to unlock region management.";
  }

  if (featureKey === "roles") {
    if (reason === PLAN_FEATURE_REASONS.OVER_CAP_GRANDFATHERED) {
      return "You have more roles than your plan allows. Existing roles are kept; upgrade to add more.";
    }
    if (reason === PLAN_FEATURE_REASONS.AT_CAP) {
      return "Role limit reached for your plan. Upgrade to Elevated or Platinum to add more roles.";
    }
    return "Upgrade to Elevated or Platinum to add more roles.";
  }

  if (featureKey === "family_login") {
    return "Family Login is available on Elevated and Platinum plans.";
  }

  if (featureKey === "participant_login") {
    return "Participant App Access is available on Elevated and Platinum plans.";
  }

  if (featureKey === "in_app_chat") {
    return "In-App Chat is available on Elevated and Platinum plans.";
  }

  return "Upgrade to Elevated or Platinum to unlock this feature.";
}
